import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import { safeGetJSON, safeSetJSON } from '@/lib/safeStorage';

export type Mood = 'calm' | 'anxious' | 'curious' | 'heavy' | 'restless';

interface MoodData {
  mood: Mood;
  date: string;
}

const STORAGE_KEY = 'human-rooms-mood';

const moods: { id: Mood; symbol: string; labelKey: string }[] = [
  { id: 'calm', symbol: '○', labelKey: 'mood_calm' },
  { id: 'anxious', symbol: '△', labelKey: 'mood_anxious' },
  { id: 'curious', symbol: '◇', labelKey: 'mood_curious' },
  { id: 'heavy', symbol: '■', labelKey: 'mood_heavy' },
  { id: 'restless', symbol: '∿', labelKey: 'mood_restless' },
];

export const useMood = () => {
  const today = new Date().toISOString().split('T')[0];
  const stored = safeGetJSON<MoodData | null>(STORAGE_KEY, null);
  if (stored && stored.date === today) return stored.mood;
  return null;
};

const MoodSelector = () => {
  const { t } = useLanguage();
  const [selectedMood, setSelectedMood] = useState<Mood | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    const stored = safeGetJSON<MoodData | null>(STORAGE_KEY, null);
    if (stored && stored.date === today) {
      setSelectedMood(stored.mood);
      setDismissed(true);
    } else {
      const timer = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(timer);
    }
  }, [today]);

  const selectMood = (mood: Mood) => {
    setSelectedMood(mood);
    safeSetJSON(STORAGE_KEY, { mood, date: today });
    setTimeout(() => setDismissed(true), 800);
  };

  if (dismissed) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="relative border border-border/20 rounded-xl bg-card/30 p-4"
        >
          <button
            onClick={() => setDismissed(true)}
            className="absolute top-3 right-3 text-muted-foreground/30 hover:text-muted-foreground transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>

          <p className="text-xs text-muted-foreground/50 mb-3 tracking-wide uppercase">
            {t('mood_question') || 'How are you feeling right now?'}
          </p>

          <div className="flex gap-2">
            {moods.map((mood) => (
              <button
                key={mood.id}
                onClick={() => selectMood(mood.id)}
                className={`flex-1 flex flex-col items-center gap-1.5 py-3 px-2 rounded-lg border transition-all duration-200 ${
                  selectedMood === mood.id
                    ? 'border-foreground/30 bg-foreground/10 scale-105'
                    : 'border-border/10 hover:border-border/30 hover:bg-card/50'
                }`}
              >
                <span className="text-lg font-light">{mood.symbol}</span>
                <span className="text-[10px] text-muted-foreground/50">
                  {t(mood.labelKey) || mood.id}
                </span>
              </button>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MoodSelector;
